import { extractOrgSlug, getOrgPath, isOrgPath } from '../../utils/routeUtils';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useOrganization, useOrganizationList, useUser } from '@clerk/clerk-react';

import { isPublicPath as isAuthPath } from '../../utils/redirectUtils';
import { toast } from 'sonner';
import { useEffect } from 'react';

/**
 * Component that handles redirecting to organization-specific URLs
 * when the user switches organizations in Clerk
 */
const OrgRedirect = () => {
  const { organization, isLoaded } = useOrganization();
  const { userMemberships, isLoaded: isOrgListLoaded } = useOrganizationList({
    userMemberships: true
  });
  const { isLoaded: isUserLoaded, isSignedIn } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  // Skip redirects for auth paths
  if (isAuthPath(currentPath)) {
    if (currentPath === '/home') {
      console.log('[OrgRedirect] Allowing access to landing page at /home');
    } else {
      console.log(`[OrgRedirect] Skipping redirect for auth path: ${currentPath}`);
    }
    return null;
  }

  // Handle auto org selection or redirect on new login
  useEffect(() => {
    if (!isUserLoaded || !isOrgListLoaded || !isLoaded) return;

    // Skip redirects for auth paths
    if (isAuthPath(currentPath)) return;

    // Only perform this check for signed-in users who are not in an org context
    if (isSignedIn && !organization && !isOrgPath(currentPath)) {
      // Check if user has any organizations
      if (userMemberships?.data && userMemberships.data.length > 0) {
        const firstOrg = userMemberships.data[0].organization;
        // Auto-select the first organization
        if (firstOrg.slug) {
          // With new route structure, just navigate to /org/<orgSlug>
          navigate({ to: '/org/$orgSlug', params: { orgSlug: firstOrg.slug } });
        }
      } else {
        // No organizations available, redirect to create one
        toast.info("Please create an organization to continue", {
          duration: 5000,
          position: "top-center"
        });
        navigate({ to: '/create-org' });
      }
    }
  }, [isUserLoaded, isOrgListLoaded, isLoaded, isSignedIn, organization, userMemberships, currentPath, navigate]);

  // Original org redirect logic
  useEffect(() => {
    if (!isLoaded) return;

    // Skip redirects for auth paths
    if (isAuthPath(currentPath)) return;

    // Get current org slug from URL (if any)
    const currentOrgSlug = extractOrgSlug(currentPath);

    // If we're already in an org path, but org doesn't match or we've switched to personal
    if (isOrgPath(currentPath)) {
      const targetOrgSlug = organization?.slug;

      // User switched to personal or org doesn't match URL
      if (!targetOrgSlug || currentOrgSlug !== targetOrgSlug) {
        // Extract the path after the org slug
        const pathParts = currentPath.split('/');
        const remainingPath = pathParts.slice(2).join('/');

        // If switched to a new org, redirect to that org's URL
        if (targetOrgSlug) {
          // With new route structure, navigate to /org/<orgSlug> + remaining path
          if (remainingPath) {
            navigate({ to: '/org/$orgSlug/$remainingPath', params: { orgSlug: targetOrgSlug, remainingPath } });
          } else {
            navigate({ to: '/org/$orgSlug', params: { orgSlug: targetOrgSlug } });
          }
        } else {
          // If switched to personal, redirect to root with the same path
          if (remainingPath) {
            navigate({ to: '/$remainingPath', params: { remainingPath } });
          } else {
            navigate({ to: '/' });
          }
        }
      }
    }
    // Only redirect to org path for app-specific paths, NOT the landing page
    else if (organization?.slug && !isOrgPath(currentPath)) {
      // With new route structure, navigate to /org/<orgSlug>
      navigate({ to: '/org/$orgSlug', params: { orgSlug: organization.slug } });
    }
  }, [organization, isLoaded, currentPath, navigate]);

  // This component doesn't render anything
  return null;
};

export default OrgRedirect; 